import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { createGradientOrSolid, overrideCssVars } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import useTheme from 'api/useTheme';
import { User } from 'common/contracts';
import {
  DEFAULT_BODY_BLOCKS_ORDER,
  EEventsType,
  ELocalStorageKeys
} from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import {
  decodeRichTextContent,
  processRichTextContent
} from 'utils/colorUtils';
import { localStorageUtil } from 'utils/localStorageUtil';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import DialogModal from 'components/DialogModal/DialogModal';
import FormHeader from 'components/FormHeader/FormHeader';
import { Form } from 'components/ui';

import { DeviceTypeController } from './Components/DeviceTypeController';
import { FormControllers } from './Components/FormControllers';
import { TitleInput } from './Components/TitleInput';
import { defaultTemplateStudioFormValues } from './defaults';
import { TemplateStudioPreview } from './Preview/Preview';
import { TemplateStudioSidebar } from './Sidebar/Sidebar';
import { templateStudioSchema } from './validation-schema';

const HeaderSecondarySection: React.FC = () => {
  return (
    <div className="flex items-center gap-[19px]">
      <DeviceTypeController />
      <FormControllers />
    </div>
  );
};

export const TemplateStudioForm: React.FC<{
  edit?: boolean;
  dup?: boolean;
}> = ({ edit = false, dup = false }) => {
  const navigate = useNavigate();
  const { publisherId, pageId } = useParams();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );
  const { getStoreScreen, getGeneralTheme } = useTheme(publisherId);
  const { getTemplate, updateTemplate, getTemplates, createTemplate } =
    useTemplatesStudio(publisherId, pageId);
  const { defaultBodyBlocksOrder, setOpenSidebarBlock } =
    useTemplateStudioState();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [
    isSubmittingActiveTemplateModalOpen,
    setIsSubmittingActiveTemplateModalOpen
  ] = useState(false);
  const BUTTON_TEXT = edit ? 'Update' : 'Save';

  const initialValues = useMemo(
    () => ({
      ...defaultTemplateStudioFormValues,
      bodyBlocksOrder: defaultBodyBlocksOrder || DEFAULT_BODY_BLOCKS_ORDER
    }),
    [defaultBodyBlocksOrder]
  );

  const form = useForm<z.infer<typeof templateStudioSchema>>({
    resolver: zodResolver(templateStudioSchema),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    formState: { isValid, isDirty }
  } = form;

  const initializeForm = useCallback(
    (data: Partial<z.infer<typeof templateStudioSchema>>) => {
      try {
        const formData = {
          ...initialValues,
          ...data,
          richText: data.richText
            ? decodeRichTextContent(data.richText)
            : initialValues.richText
        };

        form.reset(formData);
      } catch (error) {
        console.error('Error initializing form:', error);
      }
    },
    [form, initialValues]
  );

  useEffect(() => {
    if (!edit && !dup) {
      initializeForm(initialValues);
    }
    if (edit && getTemplate.data) {
      initializeForm(getTemplate.data);
    } else if (dup && getTemplate.data) {
      initializeForm({
        ...getTemplate.data,
        templateName: `${getTemplate.data.templateName}_duplicated`
      });
    }

    if ((edit || dup) && getTemplate.isError) {
      showToast({
        message: 'Could not load template',
        status: EToasterStatus.ERROR
      });
      navigate('../');
    }
  }, [
    edit,
    dup,
    getTemplate.data,
    getTemplate.isError,
    getTemplate.error,
    initialValues
  ]);

  useEffect(() => {
    return () => {
      setOpenSidebarBlock('header');
    };
  }, []);

  const isLoading = useMemo(() => {
    return (
      (edit && getTemplate.isLoading) ||
      createTemplate.isLoading ||
      updateTemplate.isLoading ||
      defaultBodyBlocksOrder === undefined
    );
  }, [
    edit,
    getTemplate.isLoading,
    createTemplate.isLoading,
    updateTemplate.isLoading,
    defaultBodyBlocksOrder
  ]);

  const isUpdateButtonEnabled = useMemo(() => {
    if (isLoading) return false;

    if (dup) return isValid;

    if (edit) {
      return isValid && isDirty;
    }
    return isValid;
  }, [isValid, isDirty, edit, dup, isLoading]);

  const handleLeave = () => {
    setIsUnsavedChangesModalOpen(false);
    navigate('../');
  };

  const handleCloseFormClick = () => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleLeave();
    }
  };

  const onSubmit = async (data: z.infer<typeof templateStudioSchema>) => {
    try {
      const apiData = {
        ...data,
        richText: processRichTextContent(data.richText)
      };

      if (edit && pageId && !dup) {
        const { templateName } = data;
        await updateTemplate.mutateAsync(
          { form: apiData, templateId: pageId },
          {
            onSuccess: () => {
              showToast({
                message: `${templateName} was updated successfully`,
                status: EToasterStatus.SUCCESS
              });
              getTemplates.refetch();
              handleLeave();
            },
            onError: () => {
              showToast({
                message: `Error updating ${templateName}`,
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        await createTemplate.mutateAsync(apiData, {
          onSuccess: () => {
            showToast({
              message: 'Template was created successfully',
              status: EToasterStatus.SUCCESS
            });
            getTemplates.refetch();
            handleLeave();
          },
          onError: (error: any) => {
            showToast({
              message: 'Error creating template',
              description:
                error?.response?.data?.message || 'Unknown error occurred',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleFormSubmit = () => {
    if (getTemplate.data?.isActive) {
      setIsSubmittingActiveTemplateModalOpen(true);
    } else {
      form.handleSubmit(onSubmit)();
    }

    trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
      publisher_id: publisherId,
      user_id: userDetails?.userId,
      company_id: userDetails?.company,
      templateId: getTemplate.data?.templateId,
      templateAction: 'save',
      isActiveTemplate: getTemplate.data?.isActive
    });

    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
      publisherId: publisherId,
      userId: userDetails?.userId,
      templateId: getTemplate.data?.templateId,
      templateAction: 'save',
      isActiveTemplate: getTemplate.data?.isActive
    });
  };

  const handleConfirmActiveTemplateSubmit = () => {
    setIsSubmittingActiveTemplateModalOpen(false);
    form.handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (!getStoreScreen.data || !getGeneralTheme.data) return;

    const cssMappingOverride = {
      '--button-text-color': getStoreScreen.data?.buttonTextColor,
      '--button-bg-color': createGradientOrSolid(
        getStoreScreen.data?.buttonColor
      ),
      '--font-family': getGeneralTheme.data?.font
    };

    overrideCssVars(cssMappingOverride, document);
  }, [
    getStoreScreen.data?.buttonColor,
    getStoreScreen.data?.buttonTextColor,
    getGeneralTheme.data?.font
  ]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [isUnsavedChangesModalOpen, isSubmittingActiveTemplateModalOpen]);

  return (
    <div className="template-studio-form-container bg-neutral-100 fixed h-[100vh] w-full top-0 left-0 z-[1000] p-1">
      <div className="bg-white h-full w-full flex overflow-scroll rounded-xl shadow-lg border border-neutral-200">
        {isLoading && (
          <div className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <CircularProgress />
          </div>
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="template-studio-form flex flex-col w-full mx-auto h-[100vh]"
          >
            <FormHeader
              TitleComponent={TitleInput}
              SecondarySection={HeaderSecondarySection}
              mainButtonText={BUTTON_TEXT}
              onCloseForm={handleCloseFormClick}
              onMainButtonClick={handleFormSubmit}
              closeFormOnSubmit={false}
              closeIcon="fa-arrow-left-to-bracket"
              isFormValid={isUpdateButtonEnabled}
              className="py-3 select-none"
            />
            <div className="form-content flex flex-grow gap-4 w-full overflow-y-hidden pt-6 px-4">
              <TemplateStudioSidebar />
              <TemplateStudioPreview isLoadingForm={isLoading} />
            </div>
          </form>
        </Form>
      </div>

      {isSubmittingActiveTemplateModalOpen && (
        <DialogModal
          isOpen={isSubmittingActiveTemplateModalOpen}
          title="Are you sure?"
          description="When you change an active template, the store will update to reflect its new values."
          modalActions={{
            onConfirm: {
              label: 'Save',
              action: handleConfirmActiveTemplateSubmit
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsSubmittingActiveTemplateModalOpen(false),
              variant: 'outline'
            }
          }}
        />
      )}

      {isUnsavedChangesModalOpen && (
        <DialogModal
          isOpen={isUnsavedChangesModalOpen}
          title={'Unsaved Changes'}
          description={
            'You’ve made changes to the template that haven’t been saved. Leaving now will discard your changes.'
          }
          modalActions={{
            onConfirm: {
              label: 'Continue',
              action: () => {
                setIsUnsavedChangesModalOpen(false);
              }
            },
            onClose: {
              label: 'Leave',
              action: handleLeave,
              variant: 'outline'
            }
          }}
        />
      )}
    </div>
  );
};
